import type { CurrentClosePeriodDto } from '@signalvote/shared';
import type { CurrentRead } from '@signalvote/data';
import { apiResponse } from './apiFetch';
import { financeReadKeys } from '../../lib/queryKeys';
import { campaignHeaders, defineQuery } from '../../lib/data';

export type { CurrentClosePeriodDto };

/**
 * CPH-001: Resolves the campaign's currently-open `FilingDeadline` for the
 * Close-the-period hub. "No open period" is the routine "nothing to close
 * right now" empty state — the backend reports it as a 200 with an empty/null
 * body (not a 404, which the browser would log as a failed request on the
 * close pages), and this read resolves it to `null` so `CloseLayout` renders
 * an honest empty state. Any non-2xx response (route mismatch, capability
 * revoked, server error) fails the read so the empty state cannot silently
 * mask a real failure.
 */
const useCurrentClosePeriodRead = defineQuery({
  key: financeReadKeys.closeCurrent,
  reads: 'GET /api/finance/close/current',
  fetch: async (_params: undefined, { signal, campaignId }): Promise<CurrentClosePeriodDto | null> => {
    const response = await apiResponse('/api/finance/close/current', { signal, headers: campaignHeaders(campaignId) });
    if (!response.ok) {
      throw new Error('Failed to fetch current close period');
    }
    // Empty body (204-style) or the literal `null` (200 + Ok(null)) both
    // mean "no open period" → null. A present body parses to the DTO.
    const text = await response.text();
    return text ? (JSON.parse(text) as CurrentClosePeriodDto) : null;
  },
  // 30 seconds — current period rarely changes within a treasurer session.
  staleTime: 30_000,
});

export function useCurrentClosePeriod(): CurrentRead<CurrentClosePeriodDto | null> {
  return useCurrentClosePeriodRead(undefined);
}
